import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Tag, Text } from "@amzn/storm-ui";

import type { KeyValue } from "../../../../types/common";
import { DateFormat, formatDate, Timezone } from "../../../../utils/date.utils";
import {
    convertAllCaps,
    convertAdditionalInfoValues,
} from "../../../../utils/unifiedContent.utils";

import {
    insightTypeToTagVariant,
    SpaceBetweenFlexBox,
    StyledAdditionalInfoText,
    StyledCardFooter,
    StyledCardHeader,
    StyledDescriptorContainer,
    StyledDescriptorPrimaryContextText,
    StyledDescriptorSecondaryContextText,
    StyledDivider,
    StyledFooterText,
    StyledInsightCard,
    StyledInsightCardBody,
    StyledTag,
} from "./styles";

interface CardLayoutV3Props {
    cardHeader: string;
    insightType?: string;
    insightTypeName?: string;
    primaryContext?: string; // Brand
    secondaryContext?: string; // Browse node
    additionalInfo?: KeyValue;
    lastModifiedDate?: string;
    description?: string;
    renderBeforeFooter?: JSX.Element;
    footer?: string;
    tag?: string;
    dataTestId: string;
    actions: JSX.Element;
}

export const CardLayoutV3: React.FC<CardLayoutV3Props> = (props) => {
    const {
        cardHeader,
        insightType,
        insightTypeName,
        primaryContext,
        secondaryContext,
        additionalInfo,
        lastModifiedDate,
        tag,
        dataTestId,
        actions,
        footer,
        description,
        renderBeforeFooter,
    } = props;

    // comment: @kevinnly, this should be moved to the insight type itself not inside cardlayout
    const renderSecondaryContext = () => {
        if (insightType === InsightType.PITCHED_CONTENT) {
            return (
                <>
                    Opportunity:{" "}
                    <span style={{ textDecoration: "underline" }}>
                        {secondaryContext}
                    </span>{" "}
                    ({additionalInfo?.opportunityStatus})
                </>
            );
        }
        return (
            <span style={{ textDecoration: "underline" }}>
                {secondaryContext}
            </span>
        );
    };

    return (
        <StyledInsightCard data-testid={dataTestId} padding={"medium"}>
            <StyledInsightCardBody direction={"column"}>
                <StyledDescriptorContainer style={{ container: "insightCard" }}>
                    <SpaceBetweenFlexBox>
                        <StyledCardHeader data-testid={`${dataTestId}-header`}>
                            {cardHeader}
                        </StyledCardHeader>
                        {tag && <Tag variant="beta" label={tag} />}
                    </SpaceBetweenFlexBox>
                    <StyledTag
                        label={insightTypeName}
                        variant={
                            insightTypeToTagVariant[insightType ?? "default"]
                        }
                        description="Description"
                    />
                    {primaryContext && (
                        <StyledDescriptorPrimaryContextText
                            data-testid={`${dataTestId}-primary`}
                        >
                            {convertAllCaps(primaryContext)}
                        </StyledDescriptorPrimaryContextText>
                    )}
                    {secondaryContext && (
                        <StyledDescriptorSecondaryContextText
                            data-testid={`${dataTestId}-secondary`}
                        >
                            {renderSecondaryContext()}
                        </StyledDescriptorSecondaryContextText>
                    )}
                    {additionalInfo &&
                        Object.entries(additionalInfo).map(
                            ([key, value]) =>
                                key !== "opportunityStatus" &&
                                value !== null &&
                                value !== undefined && (
                                    <StyledAdditionalInfoText
                                        key={key}
                                        data-testid={`${dataTestId}-${key.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {`${convertAllCaps(key)}: ${convertAdditionalInfoValues(key, value)}`}
                                    </StyledAdditionalInfoText>
                                ),
                        )}

                    {description && (
                        <Text
                            style={{ margin: "5px 0px" }}
                            color="secondary"
                            data-testid={`${dataTestId}-description`}
                        >
                            {description}
                        </Text>
                    )}
                </StyledDescriptorContainer>
                <StyledCardFooter>
                    {renderBeforeFooter && <div>{renderBeforeFooter}</div>}
                    {lastModifiedDate && (
                        <StyledFooterText data-testid={`${dataTestId}-footer`}>
                            Last updated:{" "}
                            {formatDate(
                                lastModifiedDate,
                                DateFormat.LONG_DATE_FORMAT,
                                Timezone.UTC,
                            )}
                        </StyledFooterText>
                    )}
                    {footer && (
                        <StyledFooterText
                            data-testid={`${dataTestId}-custom-footer`}
                        >
                            {footer}
                        </StyledFooterText>
                    )}
                    <StyledDivider />
                    {actions}
                </StyledCardFooter>
            </StyledInsightCardBody>
        </StyledInsightCard>
    );
};
